import React, { useEffect } from 'react'
import { View, StyleProp, ViewStyle } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import moment from 'moment'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useIsDarkMode } from '@config/theme.ts'
import { uiFlagsSelector } from '@selectors/uiFlags.ts'
import { TouchableSection } from '@components'
import { Analytics, CustomEventTypes } from '@config'
import { Icon } from '@components/base'
import { userSelector } from '@selectors/app.ts'
import Storage from '@utils/storage.ts'
import App from '@config/app.ts'
import { UiStateNames } from '@src/types.ts'
import { CheckoutKind } from '@screens/Marketplace/types/types.ts'
import { useProducts } from '@screens/Marketplace/utils/hooks.ts'

interface FreeCallTrialUpsellBannerProps {
  style?: StyleProp<ViewStyle>
}

const dismissibleAfterHours = App.build.alpha ? 1 : 72

export const FreeCallTrialUpsellBanner = ({ style }: FreeCallTrialUpsellBannerProps) => {
  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()
  const uiFlags = useSelector(uiFlagsSelector)
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const { activeSubscriptions } = useProducts()
  const coreSubscription = activeSubscriptions.find(
    (sub) => sub.id === user?.lastCoreSubscription?.id,
  )
  const bannerShownAtKey = `${Storage.FREE_CALL_TRIAL_UPSELL_BANNER_SHOWN_AT_KEY}_${user?.id}`
  const showFreeCallTrialUpsellBanner = uiFlags.showFreeCallTrialUpsellBanner

  useEffect(() => {
    if (!showFreeCallTrialUpsellBanner) {
      return
    }

    if (!Storage.get(bannerShownAtKey)) {
      Storage.set(bannerShownAtKey, moment().toISOString())
    }

    Analytics.track(CustomEventTypes.FreeCallTrialUpsellBannerViewed)
  }, [showFreeCallTrialUpsellBanner, bannerShownAtKey])

  if (!showFreeCallTrialUpsellBanner) {
    return null
  }

  const onPress = () => {
    Analytics.track(CustomEventTypes.FreeCallTrialUpsellBannerClicked)
    navigation.navigate('SteloSelectSubscription', {
      checkoutKind: CheckoutKind.UpgradeProduct,
      subscriptionId: coreSubscription?.id,
    })
  }

  const onDismiss = () => {
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.FreeCallTrialUpsellBannerDismissed,
        value: true,
      },
    })
    dispatch({
      type: 'uiFlags/updateUiFlags',
      payload: { showFreeCallTrialUpsellBanner: false },
    })
    Analytics.track(CustomEventTypes.ReferralBannerDismissed)
  }

  const bannerShownAt = Storage.get(bannerShownAtKey)

  const dismissible =
    bannerShownAt &&
    moment(bannerShownAt).isBefore(moment().subtract(dismissibleAfterHours, 'hours'))

  return (
    <View style={style}>
      <TouchableSection
        title="Ready to track your glucose?"
        subtitle="See how your body responds to every meal in real-time."
        onPress={onPress}
        onDismiss={dismissible ? onDismiss : undefined}
        style={[styles.content, isDarkMode && styles.darkContent]}
        subtitleStyle={[styles.subtitle, isDarkMode && styles.darkSubtitle]}
        labelStyle={[styles.title, isDarkMode && styles.darkTitle]}
        accessoryLeft={
          <View style={styles.iconContainer}>
            <Icon name="chart-line" style={styles.icon} />
          </View>
        }
        showRightArrow
      />
    </View>
  )
}

const themedStyles = StyleService.create({
  title: {
    color: 'theme.marketing.onText',
  },
  darkTitle: {
    color: 'theme.text.primary',
  },
  subtitle: {
    color: 'theme.marketing.subtleText',
    maxWidth: 246,
  },
  darkSubtitle: {
    color: 'theme.text.secondary',
  },
  content: {
    height: 'auto',
    backgroundColor: 'theme.marketing.lightYellow',
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
  },
  darkContent: {
    backgroundColor: 'theme.background.modal',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
    marginVertical: 'auto',
    backgroundColor: 'theme.marketing.yellow',
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  icon: {
    color: 'theme.marketing.brown',
  },
})
